import { util, CustomException } from '@tencent/ppd-base-libs';
import { IPageInfo as PageInfo } from '@/types/config-type';
import {
  getWxUrlSchema,
  getUserUrlSchema,
  getApplehubOpenlink,
} from '@/api/index';
import { isInWeixin } from '@tencent/wphk-components-common/utils/ua';
import qs from 'query-string';

/**
 * 老版本
 * 需保证actUrl有时才调用
 */
async function createUserUrlSchema(pageInfo: Pick<PageInfo, 'actUrl'>): Promise<string> {
  const { actUrl } = pageInfo;
  if (!actUrl) {
    throw new CustomException('', 'ActUrlEmptyException', JSON.stringify(pageInfo));
  }
  const [err, urlSchema] = await util.to(getUserUrlSchema(actUrl as string));
  if (err) {
    throw new CustomException(err, 'BuildUrlSchemaException', JSON.stringify(pageInfo));
  }
  return urlSchema;
}

/**
 * 新版本
 * 需保证wxAppId, wxAppPath有时才调用
 */
async function createWxUrlSchema(pageInfo: Pick<PageInfo, 'wxAppId' | 'wxAppPath' | 'wxAppQuery'>): Promise<string> {
  const { wxAppId, wxAppPath, wxAppQuery } = pageInfo;
  if (!wxAppId || !wxAppPath) {
    throw new CustomException('', 'WxAppInfoEmptyException', JSON.stringify(pageInfo));
  }
  const [err, urlSchema] = await util.to(getWxUrlSchema({
    appId: wxAppId as string,
    path: wxAppPath as string,
    query: wxAppQuery,
  }));
  if (err) {
    // 告警
    throw new CustomException(err, 'CreateWxUrlSchemaException', JSON.stringify(pageInfo));
  }
  return urlSchema;
}

/**
 * applehub 端外短路：调用后台接口生成 Apple Hub 主页 openlink（weixin://...）
 *
 * @param pageInfo 配置项（仅用于异常上下文，applehub 不消费 actUrl/wxAppPath/wxAppId 等字段）
 * @param params   外层透传的参数对象（含 adtag 等 MTN 字段）
 */
async function createApplehubOpenlink(
  pageInfo: PageInfo,
  params: { adtag?: string } & Record<string, any> = {},
): Promise<string> {
  const [err, openLink] = await util.to(getApplehubOpenlink({ adtag: params.adtag || '' }));
  if (err) {
    throw new CustomException(err, 'CreateApplehubOpenlinkException', JSON.stringify(pageInfo));
  }
  return openLink;
}

export async function createUrlInfo(pageInfo: PageInfo, params = {}, urlSchemaFromUrl?: string): Promise<{
  isInWx: boolean;
  isToH5: boolean;
  url: string;
}> {
  const { wxAppId, wxAppPath, wxAppQuery, actUrl, confId } = pageInfo;

  const isInWx = isInWeixin(); // 是否端内
  const isToH5 = !!(actUrl && !wxAppPath); // 是否跳转到h5

  const output = {
    isInWx,
    isToH5,
    url: '',
  };
  // 如果链接上有urlschema时，不用重新生成链接，可直接使用
  if (urlSchemaFromUrl) {
    output.url = urlSchemaFromUrl;
    return output;
  }

  // 端内直接跳转h5（applehub 端内场景在此命中：直接跳 actUrl）
  if (isToH5 && isInWx) {
    const url = replaceUrlSearch(actUrl, params);
    output.url = url;
    return output;
  }

  // applehub 端外短路：调 openlinkgen 接口拿 weixin:// openlink
  // 硬编码 confId 字面值；未来如需通用化（多个 confId 都走 openlinkgen）改为查注册表
  if (confId === 'applehub' && !isInWx) {
    output.url = await createApplehubOpenlink(pageInfo, params);
    return output;
  }

  // 端外拉起小程序跳转h5
  if (isToH5 && !isInWx) {
    const url = replaceUrlSearch(actUrl, params);
    const urlSchema = await createUserUrlSchema({ actUrl: url });
    output.url = urlSchema;
    return output;
  }

  const urlSchema = await createWxUrlSchema({ wxAppId, wxAppPath, wxAppQuery });
  output.url = urlSchema;
  return output;
}
/**
 * 给活动链接添加额外参数
 * 处理等号丢失问题:这里的活动链接在前序函数handleActUrl处理中，已经被qs.parase解码过了。
 * 参数值可能会带上“=”特殊字符，如果直接用item.split('=')会导致“=”丢失。
 * 例如这时候传入的活动链接为："https://prom.wechatpay.com.hk/merchant-channel/index.shtml?adtag=from_copy&shareCouponCode=U0DVwjgyyWUZbt_idxfZN7OGmPnuCUOL8OO9wd20qIo=&actId=charge_2310&spId=Rates"
 * @param urlStr 活动链接
 * @param params 额外参数
 * @returns 活动链接
 */
function replaceUrlSearch(urlStr = '', params = {}) {
  const [path, hash = ''] = urlStr.split('#');
  const [pathname] = path.split('?');
  const url = new URL(urlStr);
  const urlSearch = qs.parse(url.search);
  // 删除空值
  const newParams = { ...params };
  Object.keys(newParams).forEach((key) => {
    if (!newParams[key]) delete newParams[key];
  });
  const searchParams = Object.assign({}, urlSearch, newParams);
  const searchStr = qs.stringify(searchParams);

  let ret = pathname;
  if (searchStr) {
    ret += `?${searchStr}`;
  }
  if (hash) {
    ret += `#${hash}`;
  }
  return ret;
}
